/**
 * Editable fields of the contrat de location / prêt.
 *
 * The key set is owned by the backend (`OrdreController.CONTRAT_FIELD_KEYS`): the front
 * loads the pre-filled map, lets the user edit every value on screen, and posts the whole
 * map back — what is typed is exactly what is printed.
 */
export type ContratFields = Record<string, string | boolean>;

/** Text value of a field (checkbox fields are read through `isOn`). */
export const fieldText = (fields: ContratFields, key: string): string => {
  const value = fields[key];
  return typeof value === "string" ? value : value ? String(value) : "";
};

/** Checkbox/boolean value of a field. */
export const isOn = (fields: ContratFields, key: string): boolean => fields[key] === true;

/**
 * Driving licence details required before the contract can be generated — the garage
 * has to have seen the licence. Mirrored server-side in
 * `OrdreController._contrat_missing_fields`; keep both in sync.
 */
const REQUIRED_FIELDS: { key: string; label: string }[] = [
  { key: "cp_permis", label: "N° de permis (conducteur principal)" },
  { key: "cp_permis_le", label: "Permis délivré le (conducteur principal)" },
  { key: "cp_permis_par", label: "Permis délivré par (conducteur principal)" },
];

/** Same details for an additional driver, required only once one is named. */
const REQUIRED_IF_ADDITIONAL_DRIVER: { key: string; label: string }[] = [
  { key: "ca_permis", label: "N° de permis (conducteur additionnel)" },
  { key: "ca_permis_le", label: "Permis délivré le (conducteur additionnel)" },
  { key: "ca_permis_par", label: "Permis délivré par (conducteur additionnel)" },
];

/** Fields that still have to be filled in before generating. */
export function missingContratFields(fields: ContratFields): { key: string; label: string }[] {
  const required = fieldText(fields, "ca_nom").trim()
    ? REQUIRED_FIELDS.concat(REQUIRED_IF_ADDITIONAL_DRIVER)
    : REQUIRED_FIELDS;
  return required.filter((f) => !fieldText(fields, f.key).trim());
}
